import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

// ✅ same backend mount you used in master page
const API_BASE = "http://nissi-india.com/api/masterReport";

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const money = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const readQS = () => {
  const sp = new URLSearchParams(window.location.search);
  const obj = {};
  for (const [k, v] of sp.entries()) obj[k] = v;
  return obj;
};

export default function AssetMasterReport_Print() {
  const qs = useMemo(() => readQS(), []);

  const mode = qs.mode || "Movable";
  const printId = String(qs.id || "0"); // 0=View(Abstract) , 1=ViewAll

  // ✅ header filter fields (from querystring)
  const fromDate = qs.fromDate || "";
  const toDate = qs.toDate || "";
  // If you pass these in query later, header will show automatically:
  const branchLabel = qs.branchLabel || qs.locationText || qs.locationId || "";
  const projectLabel = qs.projectLabel || qs.project || "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  // Payload builders from URL params (query string)
  const buildMovablePayload = () => ({
    assetTypeId: qs.assetTypeId || "Select",
    assetSubTypeId: qs.assetSubTypeId || "Select",
    vehicleNameId: qs.vehicleNameId || "Select",
    fromDate: qs.fromDate || "",
    toDate: qs.toDate || "",
    makeId: qs.makeId || "Select",
    modelId: qs.modelId || "Select",
    locationId: qs.locationId || "Select",
    idleOnly: String(qs.idleOnly || "").toLowerCase() === "true",
    hypo: qs.hypo || "Select",
    calibratedDate: qs.calibratedDate || "",
    nextCalibrationDate: qs.nextCalibrationDate || "",
    printId,
  });

  const buildLandPayload = () => ({
    yearFrom: qs.yearFrom || "",
    yearTo: qs.yearTo || "",
    pattaNo: qs.pattaNo || "",
    surveyNo: qs.surveyNo || "",
    assetCode: qs.assetCode || "",
    assetClassId: qs.assetClassId || "Select",
    locationId: qs.landLocationId || "Select",
    printId,
  });

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      if (mode === "Movable") {
        const res = await axios.post(
          `${API_BASE}/movable/abstract`,
          buildMovablePayload()
        );
        if (!res.data?.success)
          throw new Error(res.data?.message || "Movable API failed");
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
        setMeta(res.data?.meta || null);
      } else if (mode === "Land") {
        const res = await axios.post(
          `${API_BASE}/land/list`,
          buildLandPayload()
        );
        if (!res.data?.success)
          throw new Error(res.data?.message || "Land API failed");
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
        setMeta(res.data?.meta || null);
      } else {
        const res = await axios.post(
          `${API_BASE}/landSales/list`,
          buildLandPayload()
        );
        if (!res.data?.success)
          throw new Error(res.data?.message || "Land Sales API failed");
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
        setMeta(res.data?.meta || null);
      }
    } catch (e) {
      setRows([]);
      setMeta(null);
      setError(e?.response?.data?.message || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Totals
  const movableTotals = useMemo(() => {
    const t = meta?.totals;
    if (t) return t;
    const purchaseValue = rows.reduce(
      (s, r) => s + toNum(r.fld_purchasevalue),
      0
    );
    const depreciation = rows.reduce(
      (s, r) => s + toNum(r.fld_depreciationvalue),
      0
    );
    const wdv = rows.reduce((s, r) => s + toNum(r.fld_wdv), 0);
    return { purchaseValue, depreciation, wdv };
  }, [rows, meta]);

  const landTotal = useMemo(
    () => rows.reduce((s, r) => s + toNum(r.fld_purchasevalue), 0),
    [rows]
  );

  const title =
    mode === "Movable"
      ? "Asset Abstract Report - Movable"
      : mode === "Land"
      ? "Asset Abstract Report - IMMovable"
      : "Asset Abstract Report - Land Sales";

  return (
    <div className="p-3 bg-white text-sm">
      {/* top label like ASPX */}
      <div className="flex justify-end mb-2">
        <div className="text-xs text-gray-700">INFRA SCREEN : D-13/1</div>
      </div>

      {/* buttons (hidden on print) */}
      <div className="no-print flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => window.print()}
          className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50"
        >
          Print
        </button>
        <button
          type="button"
          onClick={fetchData}
          className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {/* ✅ NEW HEADER YOU ASKED (added) */}
      <div className="border-2 border-black mb-0">
        <div className="text-center py-3 border-b border-black">
          <h1 className="text-lg font-bold text-red-600">Nissi Infotech</h1>
          <div className="text-sm">Nissi</div>
          <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>
          <div className="text-xs">
            3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,India
          </div>
          <div className="text-xs">
            Phone No : 9003744644 Email : infraconsoft-implementation@nissiinfotech.in
          </div>
        </div>

        <div className="text-center py-2 border-b border-black font-bold">
          {title}
        </div>

        {/* ✅ Filter Header */}
        <div className="px-3 py-2 border-b border-black text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <b>From :</b> {meta?.fromdate || fromDate || "-"}
            </div>
            <div>
              <b>To :</b> {meta?.todate || toDate || "-"}
            </div>
            <div>
              <b>Branch :</b> {branchLabel || "-"}
            </div>
            <div>
              <b>Project :</b> {projectLabel || "-"}
            </div>
          </div>

          <div className="mt-1 text-xs text-gray-700">
            <b>View :</b> {printId === "1" ? "All" : "Abstract"} &nbsp; | &nbsp;
            <b>Mode :</b> {mode}
          </div>
        </div>
      </div>

      {/* ✅ REPORT BODY */}
      <div className="border border-black border-t-0">
        {loading && <div className="p-3 text-center">Loading...</div>}
        {error && (
          <div className="p-3 text-red-700 border-b border-black">
            Error: {error}
          </div>
        )}

        {/* MOVABLE TABLE */}
        {mode === "Movable" && (
          <div className="overflow-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-black">
                  <th className="border-r border-black p-1">S.No</th>
                  <th className="border-r border-black p-1">Subtype No</th>
                  <th className="border-r border-black p-1">Description</th>
                  <th className="border-r border-black p-1 text-right">Qty</th>
                  <th className="border-r border-black p-1 text-right">
                    Purchase
                  </th>
                  <th className="border-r border-black p-1 text-right">
                    Depreciation
                  </th>
                  <th className="p-1 text-right">WDV</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="p-3 text-center text-gray-500"
                    >
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx} className="border-b border-gray-300">
                      <td className="border-r border-black p-1 text-center">
                        {r.fld_AssetIndex ?? idx + 1}
                      </td>
                      <td className="border-r border-black p-1">
                        {r.fld_subtypeNo ?? ""}
                      </td>
                      <td className="border-r border-black p-1">
                        {r.AssetType ?? r.fld_assettypename ?? ""}
                      </td>
                      <td className="border-r border-black p-1 text-right">
                        {toNum(r.Qty).toLocaleString("en-IN")}
                      </td>
                      <td className="border-r border-black p-1 text-right">
                        {money(r.fld_purchasevalue)}
                      </td>
                      <td className="border-r border-black p-1 text-right">
                        {money(r.fld_depreciationvalue)}
                      </td>
                      <td className="p-1 text-right">{money(r.fld_wdv)}</td>
                    </tr>
                  ))
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr className="border-t border-black font-semibold">
                    <td
                      colSpan={4}
                      className="border-r border-black p-1 text-right"
                    >
                      TOTAL :
                    </td>
                    <td className="border-r border-black p-1 text-right">
                      {money(movableTotals.purchaseValue)}
                    </td>
                    <td className="border-r border-black p-1 text-right">
                      {money(movableTotals.depreciation)}
                    </td>
                    <td className="p-1 text-right">
                      {money(movableTotals.wdv)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* LAND / LAND SALES TABLE */}
        {mode !== "Movable" && (
          <div className="overflow-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-black">
                  <th className="border-r border-black p-1">S.No</th>
                  <th className="border-r border-black p-1">Asset Code</th>
                  <th className="border-r border-black p-1">Patta No</th>
                  <th className="border-r border-black p-1">Survey No</th>
                  <th className="border-r border-black p-1 text-center">
                    Extent
                  </th>
                  <th className="border-r border-black p-1 text-center">
                    Acquisition
                  </th>
                  <th className="border-r border-black p-1 text-right">
                    Purchase
                  </th>
                  <th className="border-r border-black p-1">Seller</th>
                  <th className="border-r border-black p-1">Present Holder</th>
                  <th className="p-1">Original Doc</th>
                </tr>
              </thead>
              <tbody>
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="p-3 text-center text-gray-500"
                    >
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx} className="border-b border-gray-300">
                      <td className="border-r border-black p-1 text-center">
                        {idx + 1}
                      </td>
                      <td className="border-r border-black p-1">
                        {r.fld_assetcode ?? ""}
                      </td>
                      <td className="border-r border-black p-1">
                        {r.fld_pattano ?? ""}
                      </td>
                      <td className="border-r border-black p-1">
                        {r.fld_surveyno ?? ""}
                      </td>
                      <td className="border-r border-black p-1 text-center">
                        {r.fld_extend ?? ""}
                      </td>
                      <td className="border-r border-black p-1 text-center">
                        {r.fld_acquisitiondate ?? ""}
                      </td>
                      <td className="border-r border-black p-1 text-right">
                        {money(r.fld_purchasevalue)}
                      </td>
                      <td className="border-r border-black p-1">
                        {r.fld_sellername ?? ""}
                      </td>
                      <td className="border-r border-black p-1">
                        {r.fld_propertyname ?? ""}
                      </td>
                      <td className="p-1">{r.fld_originaldoc ?? ""}</td>
                    </tr>
                  ))
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr className="border-t border-black font-semibold">
                    <td
                      colSpan={6}
                      className="border-r border-black p-1 text-right"
                    >
                      TOTAL PURCHASE :
                    </td>
                    <td className="border-r border-black p-1 text-right">
                      {money(landTotal)}
                    </td>
                    <td colSpan={3} className="p-1" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        <div className="border-t border-black flex">
          <div className="flex-1 p-2 text-xs underline">
            Report generated by ERP-Software - www.infraconsoft.com owned by
            www.nissiinfotech.com
          </div>
          <div className="border-l border-black p-2 text-xs">Page 1 of 1</div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: #fff; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
